/**
 * Get Schema Tool — "Sinks Not Pipes"
 *
 * Returns a compact, LLM-friendly representation of a canonical Zod schema.
 * Derived programmatically via z.toJSONSchema() — never hardcoded — so schema
 * and documentation can never drift from each other.
 *
 * Used by the foreman during build_specialist_prompt to inject canonical schema
 * references into specialist prompts. This is the source-of-truth injection layer
 * for swp-bf6: specialists receive the actual schema, not a prose copy.
 */
import { McpServer } from '@modelcontextprotocol/sdk/server/mcp.js';
import { z } from 'zod';
import {
  WorkflowStepSchema,
  WorkflowDefinitionSchema,
  WorkflowFieldSchema,
  WorkflowActionSchema,
  authConfigSchema,
} from '@stackwright-pro/types';
import {
  SUPPORTED_SCHEMA_NAMES,
  FIELD_SYNONYMS,
  type SchemaName,
} from './validate-yaml-fragment.js';

// Re-export so callers can import from one place
export { SUPPORTED_SCHEMA_NAMES };

// ---------------------------------------------------------------------------
// Inline navigation schema (mirrors validate-yaml-fragment.ts — DRY violation
// risk is acceptable here: both files are small and colocated. The alternative
// (a shared-types file for just two schemas) would be over-engineering.)
// ---------------------------------------------------------------------------

const NavigationLinkSchema: z.ZodType<unknown> = z.lazy(() =>
  z.object({
    label: z.string().min(1),
    href: z.string().min(1),
    children: z.array(NavigationLinkSchema).optional(),
  })
);

const NavigationSectionSchema = z.object({
  section: z.string().min(1),
  items: z.array(NavigationLinkSchema),
});

const NavigationItemSchema = z.union([
  NavigationLinkSchema as z.ZodObject<any>,
  NavigationSectionSchema,
]);

// ---------------------------------------------------------------------------
// Schema registry (same set as validate-yaml-fragment)
// ---------------------------------------------------------------------------

function getZodSchema(name: SchemaName): z.ZodTypeAny {
  switch (name) {
    case 'workflow_step':
      return WorkflowStepSchema;
    case 'workflow_definition':
      return WorkflowDefinitionSchema;
    case 'workflow_field':
      return WorkflowFieldSchema;
    case 'workflow_action':
      return WorkflowActionSchema;
    case 'navigation_item':
      return NavigationItemSchema;
    case 'auth_config':
      return authConfigSchema;
  }
}

// ---------------------------------------------------------------------------
// JSON Schema → compact text format
// ---------------------------------------------------------------------------

type JsonSchemaNode = {
  type?: string | string[];
  properties?: Record<string, JsonSchemaNode>;
  required?: string[];
  enum?: unknown[];
  const?: unknown;
  oneOf?: JsonSchemaNode[];
  anyOf?: JsonSchemaNode[];
  items?: JsonSchemaNode;
  maxLength?: number;
  minLength?: number;
  pattern?: string;
  minimum?: number;
  maximum?: number;
  additionalProperties?: boolean;
  description?: string;
};

function formatType(node: JsonSchemaNode, depth = 0): string {
  if (node.const !== undefined) return JSON.stringify(node.const);
  if (node.enum) return node.enum.map((v) => JSON.stringify(v)).join(' | ');

  if (node.oneOf || node.anyOf) {
    const branches = node.oneOf ?? node.anyOf ?? [];
    // Discriminated union — show all variant types
    return branches
      .map((b) => {
        const disc = b.properties
          ? Object.entries(b.properties)
              .filter(([, v]) => v.const !== undefined)
              .map(([k, v]) => `${k}: ${JSON.stringify(v.const)}`)
              .join(', ')
          : '';
        return disc ? `{ ${disc}, ... }` : '{ ... }';
      })
      .join(' | ');
  }

  if (node.type === 'array' && node.items) {
    return `${formatType(node.items, depth)}[]`;
  }

  if (node.type === 'object' && node.properties && depth < 2) {
    const inner = Object.entries(node.properties)
      .map(([k, v]) => `${k}: ${formatType(v, depth + 1)}`)
      .join(', ');
    return `{ ${inner} }`;
  }

  const base = Array.isArray(node.type) ? node.type.join(' | ') : (node.type ?? 'unknown');

  const constraints: string[] = [];
  if (node.maxLength !== undefined) constraints.push(`max ${node.maxLength} chars`);
  if (node.minLength !== undefined) constraints.push(`min ${node.minLength} chars`);
  if (node.pattern) constraints.push(`pattern: ${node.pattern}`);
  if (node.minimum !== undefined) constraints.push(`min ${node.minimum}`);
  if (node.maximum !== undefined) constraints.push(`max ${node.maximum}`);

  return constraints.length > 0 ? `${base} (${constraints.join(', ')})` : base;
}

function formatObjectSchema(
  node: JsonSchemaNode,
  schemaName: string,
  synonyms: Record<string, string>
): string {
  const lines: string[] = [];
  const displayName = schemaName
    .split('_')
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join('');

  lines.push(`${displayName}:`);

  // Handle discriminated unions (oneOf/anyOf at the top level)
  if (node.oneOf || node.anyOf) {
    const branches = node.oneOf ?? node.anyOf ?? [];
    lines.push('  Discriminated union — choose one variant:');
    for (const branch of branches) {
      const req = new Set(branch.required ?? []);
      const disc = Object.entries(branch.properties ?? {})
        .filter(([, v]) => v.const !== undefined)
        .map(([k, v]) => `${k}: ${JSON.stringify(v.const)}`)
        .join(', ');
      lines.push(`  VARIANT (${disc}):`);
      for (const [field, fieldNode] of Object.entries(branch.properties ?? {})) {
        if (fieldNode.const !== undefined) continue; // discriminant shown in header
        const isRequired = req.has(field);
        const hint = synonyms[field] ? `  -- WARNING: use ${field}: not ${synonyms[field]}` : '';
        lines.push(
          `    ${isRequired ? 'REQUIRED' : 'optional'}: ${field}: ${formatType(fieldNode)}${hint}`
        );
      }
    }
    return lines.join('\n');
  }

  // Object schema
  const required = new Set(node.required ?? []);
  const properties = node.properties ?? {};

  const requiredFields = Object.entries(properties).filter(([k]) => required.has(k));
  const optionalFields = Object.entries(properties).filter(([k]) => !required.has(k));

  if (requiredFields.length > 0) {
    lines.push('  REQUIRED:');
    for (const [field, fieldNode] of requiredFields) {
      const typeStr = formatType(fieldNode);
      const warn = synonyms[field] ? `  -- CAUTION: this schema uses ${field}` : '';
      // Attach warnings for the *correct* field that is also a synonym target
      // Actually: warn for wrong names that newcomers use instead of this field
      lines.push(`    ${field}: ${typeStr}${warn}`);
    }
  }

  if (optionalFields.length > 0) {
    lines.push('  OPTIONAL:');
    for (const [field, fieldNode] of optionalFields) {
      const typeStr = formatType(fieldNode);
      lines.push(`    ${field}: ${typeStr}`);
    }
  }

  return lines.join('\n');
}

// ---------------------------------------------------------------------------
// Exported handler
// ---------------------------------------------------------------------------

export interface GetSchemaInput {
  schemaName: string;
}

export interface GetSchemaResult {
  schemaName: string;
  summary: string;
  synonymWarnings: string[];
  tokenEstimate: number;
}

export function handleGetSchema(input: GetSchemaInput): GetSchemaResult | { error: string } {
  const { schemaName } = input;

  if (!(SUPPORTED_SCHEMA_NAMES as readonly string[]).includes(schemaName)) {
    return {
      error: `Unknown schemaName: "${schemaName}". Supported: ${SUPPORTED_SCHEMA_NAMES.join(', ')}`,
    };
  }

  const name = schemaName as SchemaName;
  const schema = getZodSchema(name);
  const synonyms = FIELD_SYNONYMS[name];

  // Derive schema summary from Zod via JSON Schema conversion
  let jsonSchema: JsonSchemaNode;
  try {
    // toJSONSchema strips $schema wrapper — we work with the raw shape
    const raw = z.toJSONSchema(schema, { unrepresentable: 'any' }) as JsonSchemaNode;
    // Remove $schema field (not useful for LLM consumption)
    jsonSchema = raw;
  } catch {
    // Fallback for lazy schemas (NavigationItem uses z.lazy)
    jsonSchema = { type: 'object', description: 'Schema introspection unavailable for this type' };
  }

  const summary = formatObjectSchema(jsonSchema, name, synonyms);

  // Append synonym warnings as a dedicated section
  const synonymWarnings = Object.entries(synonyms).map(
    ([wrong, correct]) => `  WARNING: use "${correct}" — NOT "${wrong}"`
  );

  // Rough token estimate: ~4 chars per token
  const fullText = [
    summary,
    '',
    'FIELD NAME WARNINGS (common drift patterns):',
    ...synonymWarnings,
  ].join('\n');
  const tokenEstimate = Math.ceil(fullText.length / 4);

  return {
    schemaName: name,
    summary,
    synonymWarnings,
    tokenEstimate,
  };
}

// ---------------------------------------------------------------------------
// Convenience: get schemas for a set of phase-relevant schema names
// ---------------------------------------------------------------------------

/** Maps pipeline phase → schema names relevant to that phase */
export const PHASE_SCHEMA_NAMES: Partial<Record<string, readonly SchemaName[]>> = {
  workflow: ['workflow_step', 'workflow_definition', 'workflow_field', 'workflow_action'],
  pages: ['navigation_item'],
  dashboard: ['navigation_item'],
  polish: ['navigation_item'],
  auth: ['auth_config'],
};

/**
 * Returns a compact schema reference block for injection into specialist prompts.
 * Called by handleBuildSpecialistPrompt — gives specialists the canonical
 * schema contract without requiring MCP access in sub-agents.
 */
export function buildSchemaReferenceBlock(phase: string): string | null {
  const schemaNames = PHASE_SCHEMA_NAMES[phase];
  if (!schemaNames || schemaNames.length === 0) return null;

  const sections: string[] = [
    'CANONICAL_SCHEMA_REFERENCE (authoritative — use exact field names below):',
    '',
  ];

  for (const name of schemaNames) {
    const result = handleGetSchema({ schemaName: name });
    if ('error' in result) continue;

    sections.push(result.summary);

    if (result.synonymWarnings.length > 0) {
      sections.push('  Common mistakes to avoid:');
      sections.push(...result.synonymWarnings);
    }
    sections.push('');
  }

  return sections.join('\n');
}

// ---------------------------------------------------------------------------
// MCP tool registration
// ---------------------------------------------------------------------------

export function registerGetSchemaTool(server: McpServer): void {
  server.tool(
    'stackwright_pro_get_schema',
    'Returns a compact, LLM-friendly summary of a canonical schema derived programmatically from Zod. Includes required/optional fields, types, constraints, and "did you mean?" warnings for common field-name drift. Use before authoring YAML to confirm exact field names.',
    {
      schemaName: z
        .enum(SUPPORTED_SCHEMA_NAMES)
        .describe(
          'Schema to summarize. One of: workflow_step, workflow_definition, workflow_field, workflow_action, navigation_item, auth_config'
        ),
    },
    async ({ schemaName }) => {
      const result = handleGetSchema({ schemaName });
      if ('error' in result) {
        return {
          content: [{ type: 'text', text: JSON.stringify({ error: result.error }) }],
          isError: true,
        };
      }
      return {
        content: [
          {
            type: 'text',
            text: JSON.stringify(result, null, 2),
          },
        ],
      };
    }
  );
}
